/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import { saveAs } from "file-saver";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import CardContent from "@mui/material/CardContent";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDProgress from "components/MDProgress";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import { API_ESA_OFFICE } from "utils";

function PerdinasDetail() {
  const [loading, setLoading] = useState(false);
  const [dataRekapDinas, setDataRekapDinas] = useState(null);

  const [userId, setUserId] = useState(null);
  const [userDinasId, setUserDinasId] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Ambil query parameters
  const { dinasId } = useParams();
  const tanggalAwal = searchParams.get("tanggalAwal");
  const tanggalAkhir = searchParams.get("tanggalAkhir");
  const [tanggal_awal, setTanggalAwal] = useState(tanggalAwal);
  const [tanggal_akhir, setTanggalAkhir] = useState(tanggalAkhir);
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    setUserId(user.uid_RSS || user.uid);
    setUserDinasId(user.dinas_id);
  }, []);

  const fetchRekapDinas = () => {
    if (!dinasId || !tanggal_awal || !tanggal_akhir) {
      swal("Error", "Data dinas atau tanggal tidak valid", "error");
      return;
    }

    setLoading(true);
    axios
      .get(
        `${API_ESA_OFFICE}laporan-harian/dinas/${dinasId}/laporan?tanggal_mulai=${tanggal_awal}&tanggal_akhir=${tanggal_akhir}`
      )
      .then((res) => {
        setDataRekapDinas(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching rekap dinas:", err);
        setLoading(false);
        swal("Error", "Gagal mengambil data rekap dinas", "error");
      });
  };

  const handleTanggalChange = () => {
    if (tanggalAwal && tanggalAkhir) {
      fetchRekapDinas();
    }
  };

  // Auto-fetch data ketika tanggal berubah
  useEffect(() => {
    if (dinasId && tanggal_awal && tanggal_akhir && !loading) {
      fetchRekapDinas();
    }
  }, [dinasId, tanggal_awal, tanggal_akhir]);

  const handleDetailClick = (pegawai) => {
    // Navigate ke halaman detail dengan parameter uid dan query parameters untuk tanggal
    navigate(
      `/daily-performance/laporan-harian/perorang-detail/${pegawai.uid_RSS}?tanggal_awal=${tanggalAwal}&tanggal_akhir=${tanggalAkhir}&nama=${pegawai.nama_pegawai}`
    );
  };

  // Fungsi untuk export Excel
  const handleExportExcel = () => {
    if (!dataRekapDinas?.data_pegawai?.length) {
      swal({
        title: "Info",
        text: "Tidak ada data untuk di-export",
        icon: "info",
        button: "OK",
      });
      return;
    }

    // Buat HTML table untuk Excel
    const tableHeaders = [
      "No",
      "Nama Pegawai",
      "Jabatan",
      "Bidang",
      "Pangkat",
      "Jumlah Laporan",
      "Total Kegiatan",
      "Rata-rata Kegiatan/Laporan",
      "Hari Kerja",
      "Persentase Kepatuhan",
    ];

    let tableRows = "";
    // Urutkan data untuk export Excel juga
    const sortedData = [...dataRekapDinas.data_pegawai].sort((a, b) => {
      const persentaseA = parseFloat(a.persentase_kepatuhan_individual) || 0;
      const persentaseB = parseFloat(b.persentase_kepatuhan_individual) || 0;
      return persentaseB - persentaseA; // Descending order (tertinggi ke terendah)
    });

    sortedData.forEach((pegawai, index) => {
      tableRows += `
        <tr>
          <td>${index + 1}</td>
          <td>${pegawai.nama_pegawai || ""}</td>
          <td>${pegawai.nama_jabatan || ""}</td>
          <td>${pegawai.nama_bidang || "-"}</td>
          <td>${pegawai.pangkat || ""}</td>
          <td>${pegawai.jumlah_laporan || 0}</td>
          <td>${pegawai.total_kegiatan || 0}</td>
          <td>${pegawai.rata_rata_kegiatan_per_laporan || 0}</td>
          <td>${dataRekapDinas?.periode?.hari_kerja || 0}</td>
          <td>${pegawai.persentase_kepatuhan_individual || "0.00"}%</td>
        </tr>
      `;
    });

    const htmlContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="UTF-8">
          <title>Rekap Laporan Dinas - ${tanggalAwal} s/d ${tanggalAkhir}</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
          </style>
        </head>
        <body>
          <h2>Rekap Laporan Dinas - ${dataRekapDinas.nama_dinas}</h2>
          <h3>Periode: ${new Date(tanggalAwal).toLocaleDateString(
            "id-ID"
          )} s/d ${new Date(tanggalAkhir).toLocaleDateString("id-ID")}</h3>
          <h3>Statistik:</h3>
          <ul>
            <li>Total Pegawai: ${dataRekapDinas.statistik.total_pegawai}</li>
            <li>Total Laporan: ${dataRekapDinas.statistik.total_laporan}</li>
            <li>Total Kegiatan: ${dataRekapDinas.statistik.total_kegiatan}</li>
            <li>Rata-rata Kegiatan per Laporan: ${
              dataRekapDinas.statistik.rata_rata_kegiatan_per_laporan
            }</li>
            <li>Rata-rata Kegiatan per Hari: ${
              dataRekapDinas.statistik.rata_rata_kegiatan_per_hari
            }</li>
            <li>Persentase Kepatuhan: ${
              dataRekapDinas.statistik.persentase_kepatuhan
            }%</li>
            <li>Total Laporan Seharusnya: ${
              dataRekapDinas.statistik.total_laporan_seharusnya
            }</li>
            <li>Hari Kerja: ${dataRekapDinas.periode.hari_kerja}</li>
          </ul>
          <table>
            <thead>
              <tr>
                ${tableHeaders.map((header) => `<th>${header}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], {
      type: "application/vnd.ms-excel;charset=utf-8",
    });
    saveAs(blob, `rekap-laporan-dinas-${tanggalAwal}-${tanggalAkhir}.xls`);
  };

  // Kolom untuk tabel data pegawai
  const columns = [
    { Header: "No", accessor: "index", width: "5%" },
    {
      Header: "Profil",
      accessor: "profil",
      width: "8%",
      Cell: ({ row }) => (
        <MDBox display="flex" alignItems="center">
          <img
            src={`https://smartdh.rotendaokab.go.id/uploads/${row.original.uid_RSS}.jpg`}
            alt={row.original.nama_pegawai}
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              objectFit: "cover",
              border: "2px solid #e0e0e0",
            }}
            onError={(e) => {
              // Jika gambar tidak ditemukan, tampilkan icon default
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
          <MDBox
            display="none"
            alignItems="center"
            justifyContent="center"
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: "grey.200",
              color: "grey.500",
            }}
          >
            <Icon sx={{ fontSize: 20 }}>person</Icon>
          </MDBox>
        </MDBox>
      ),
    },
    { Header: "Nama Pegawai", accessor: "nama_pegawai", width: "15%" },
    { Header: "Jabatan", accessor: "nama_jabatan", width: "12%" },
    { Header: "Bidang", accessor: "nama_bidang", width: "12%" },
    { Header: "Pangkat", accessor: "pangkat", width: "8%" },
    { Header: "Jumlah Laporan", accessor: "jumlah_laporan", width: "10%" },
    { Header: "Total Kegiatan", accessor: "total_kegiatan", width: "10%" },
    {
      Header: "Rata-rata/Laporan",
      accessor: "rata_rata_kegiatan_per_laporan",
      width: "10%",
    },
    { Header: "Hari Kerja", accessor: "hari_kerja", width: "8%" },
    {
      Header: "Persentase Kepatuhan",
      accessor: "persentase_kepatuhan_individual",
      width: "12%",
      Cell: ({ value }) => (
        <Chip
          label={`${value}%`}
          color={
            parseFloat(value) >= 80
              ? "success"
              : parseFloat(value) >= 60
              ? "warning"
              : "error"
          }
          size="small"
          variant="filled"
        />
      ),
    },
    {
      Header: "Detail",
      accessor: "detail",
      width: "8%",
      Cell: ({ row }) => (
        <MDButton
          variant="gradient"
          color="info"
          size="small"
          onClick={() => handleDetailClick(row.original)}
          sx={{ minWidth: "auto", px: 1 }}
        >
          <Icon sx={{ fontSize: 16 }}>visibility</Icon>
        </MDButton>
      ),
    },
  ];

  // Rows untuk tabel data pegawai
  const rows =
    dataRekapDinas?.data_pegawai
      ?.sort((a, b) => {
        // Urutkan berdasarkan persentase kepatuhan dari tertinggi ke terendah
        const persentaseA = parseFloat(a.persentase_kepatuhan_individual) || 0;
        const persentaseB = parseFloat(b.persentase_kepatuhan_individual) || 0;
        return persentaseB - persentaseA; // Descending order (tertinggi ke terendah)
      })
      ?.map((pegawai, index) => ({
        index: index + 1,
        uid_RSS: pegawai.uid_RSS,
        nama_pegawai: pegawai.nama_pegawai,
        nama_jabatan: pegawai.nama_jabatan,
        nama_bidang: pegawai.nama_bidang || "-",
        pangkat: pegawai.pangkat,
        jumlah_laporan: pegawai.jumlah_laporan,
        total_kegiatan: pegawai.total_kegiatan,
        rata_rata_kegiatan_per_laporan: pegawai.rata_rata_kegiatan_per_laporan,
        hari_kerja: dataRekapDinas?.periode?.hari_kerja || 0,
        persentase_kepatuhan_individual:
          pegawai.persentase_kepatuhan_individual,
      })) || [];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox my={3}>
        <Grid container spacing={3}>
          {/* Statistik Card dengan Filter Tanggal */}
          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={3}
                >
                  <MDTypography variant="h6" gutterBottom>
                    Rekap Laporan Harian Dinas
                  </MDTypography>
                  <MDBox display="flex" gap={2}>
                    <MDInput
                      type="date"
                      label="Tanggal Awal"
                      value={tanggal_awal}
                      onChange={(e) => setTanggalAwal(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    <MDInput
                      type="date"
                      label="Tanggal Akhir"
                      value={tanggal_akhir}
                      onChange={(e) => setTanggalAkhir(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={() => navigate(-1)}
                    >
                      <Icon>arrow_back</Icon>&nbsp;Kembali
                    </MDButton>
                  </MDBox>
                </MDBox>

                {dataRekapDinas && (
                  <>
                    <MDTypography variant="button" color="text" gutterBottom>
                      {dataRekapDinas.nama_dinas}{" "}
                    </MDTypography>
                    <MDTypography variant="button" color="text" gutterBottom>
                      Periode:{" "}
                      {new Date(
                        dataRekapDinas.periode.tanggal_mulai
                      ).toLocaleDateString("id-ID")}{" "}
                      s/d{" "}
                      {new Date(
                        dataRekapDinas.periode.tanggal_akhir
                      ).toLocaleDateString("id-ID")}{" "}
                      ({dataRekapDinas.periode.hari_kerja} hari kerja)
                    </MDTypography>

                    <Grid container spacing={3} mt={2}>
                      <Grid item xs={12} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="info">
                            {dataRekapDinas.statistik.total_pegawai}
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Total Pegawai
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="success">
                            {dataRekapDinas.statistik.total_laporan}
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Total Laporan
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="primary">
                            {dataRekapDinas.statistik.total_kegiatan}
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Total Kegiatan
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="warning">
                            {
                              dataRekapDinas.statistik
                                .rata_rata_kegiatan_per_laporan
                            }
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Rata-rata/Laporan
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="secondary">
                            {
                              dataRekapDinas.statistik
                                .rata_rata_kegiatan_per_hari
                            }
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Rata-rata/Hari
                          </MDTypography>
                        </MDBox>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <MDBox textAlign="center">
                          <MDTypography variant="h4" color="dark">
                            {dataRekapDinas.statistik.total_laporan_seharusnya}
                          </MDTypography>
                          <MDTypography variant="button" color="text">
                            Target Laporan
                          </MDTypography>
                        </MDBox>
                      </Grid>
                    </Grid>

                    {/* Progress Bar */}
                    <MDBox mt={3}>
                      <MDBox
                        display="flex"
                        justifyContent="space-between"
                        mb={1}
                      >
                        <MDTypography variant="button" color="text">
                          Persentase Kepatuhan
                        </MDTypography>
                        <MDTypography
                          variant="button"
                          color={
                            dataRekapDinas.statistik.persentase_kepatuhan >= 80
                              ? "success"
                              : dataRekapDinas.statistik.persentase_kepatuhan >=
                                60
                              ? "warning"
                              : "error"
                          }
                        >
                          {dataRekapDinas.statistik.persentase_kepatuhan}%
                        </MDTypography>
                      </MDBox>
                      <MDProgress
                        variant="determinate"
                        value={dataRekapDinas.statistik.persentase_kepatuhan}
                        color={
                          dataRekapDinas.statistik.persentase_kepatuhan >= 80
                            ? "success"
                            : dataRekapDinas.statistik.persentase_kepatuhan >=
                              60
                            ? "warning"
                            : "error"
                        }
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </MDBox>
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>

          {/* Tabel Data Pegawai */}
          {dataRekapDinas && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3}>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={2}
                  >
                    <MDBox>
                      <MDTypography variant="h6" color="text">
                        Data Pegawai:{" "}
                        {new Date(tanggalAwal).toLocaleDateString("id-ID", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}{" "}
                        s/d{" "}
                        {new Date(tanggalAkhir).toLocaleDateString("id-ID", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        })}
                      </MDTypography>
                      <MDTypography variant="caption" color="text">
                        Total: {dataRekapDinas.data_pegawai.length} pegawai
                      </MDTypography>
                    </MDBox>
                    <MDButton
                      variant="outlined"
                      color="dark"
                      onClick={handleExportExcel}
                      disabled={loading}
                    >
                      <Icon>download</Icon>
                      &nbsp;Export Excel
                    </MDButton>
                  </MDBox>
                  {isMobile ? (
                    <MDBox display="flex" flexDirection="column" gap={1}>
                      {loading && (
                        <MDTypography variant="button" color="text.secondary">
                          Memuat...
                        </MDTypography>
                      )}
                      {!loading &&
                        (dataRekapDinas?.data_pegawai || []).map(
                          (pegawai, index) => {
                            const persen =
                              parseFloat(
                                pegawai.persentase_kepatuhan_individual
                              ) || 0;
                            return (
                              <Card
                                key={`${
                                  pegawai.uid_RSS ||
                                  pegawai.nama_pegawai ||
                                  "row"
                                }-${index}`}
                              >
                                <CardContent>
                                  <Grid
                                    container
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    <Grid item xs={4}>
                                      <MDBox
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                      >
                                        <img
                                          src={`https://smartdh.rotendaokab.go.id/uploads/${pegawai.uid_RSS}.jpg`}
                                          alt={pegawai.nama_pegawai}
                                          style={{
                                            width: 72,
                                            height: 72,
                                            borderRadius: "50%",
                                            objectFit: "cover",
                                            border: "2px solid #e0e0e0",
                                          }}
                                          onError={(e) => {
                                            e.target.style.display = "none";
                                            e.target.nextSibling.style.display =
                                              "flex";
                                          }}
                                        />
                                        <MDBox
                                          display="none"
                                          alignItems="center"
                                          justifyContent="center"
                                          sx={{
                                            width: 72,
                                            height: 72,
                                            borderRadius: "50%",
                                            backgroundColor: "grey.200",
                                            color: "grey.500",
                                          }}
                                        >
                                          <Icon sx={{ fontSize: 36 }}>
                                            person
                                          </Icon>
                                        </MDBox>
                                      </MDBox>
                                    </Grid>
                                    <Grid item xs={8}>
                                      <MDTypography
                                        variant="button"
                                        fontWeight="bold"
                                      >
                                        {index + 1}. {pegawai.nama_pegawai}
                                      </MDTypography>
                                      <MDBox mt={0.5}>
                                        <MDTypography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          {pegawai.nama_jabatan}
                                        </MDTypography>
                                      </MDBox>
                                      <MDBox>
                                        <MDTypography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          Bidang: {pegawai.nama_bidang || "-"} •
                                          Pangkat: {pegawai.pangkat || "-"}
                                        </MDTypography>
                                      </MDBox>
                                      <MDBox
                                        mt={1}
                                        display="flex"
                                        flexWrap="wrap"
                                        gap={0.5}
                                      >
                                        <Chip
                                          label={`Laporan: ${
                                            pegawai.jumlah_laporan || 0
                                          }`}
                                          size="small"
                                        />
                                        <Chip
                                          label={`Kegiatan: ${
                                            pegawai.total_kegiatan || 0
                                          }`}
                                          color="info"
                                          size="small"
                                        />
                                        <Chip
                                          label={`Rata2/Laporan: ${
                                            pegawai.rata_rata_kegiatan_per_laporan ||
                                            0
                                          }`}
                                          color="secondary"
                                          size="small"
                                        />
                                        <Chip
                                          label={`Hari Kerja: ${
                                            dataRekapDinas?.periode
                                              ?.hari_kerja || 0
                                          }`}
                                          size="small"
                                        />
                                      </MDBox>
                                      <MDBox mt={1.25}>
                                        <MDTypography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          Persentase Kepatuhan
                                        </MDTypography>
                                        <MDBox mt={0.5}>
                                          <MDProgress
                                            variant="gradient"
                                            value={persen}
                                            color={
                                              persen >= 80
                                                ? "success"
                                                : persen >= 60
                                                ? "warning"
                                                : "error"
                                            }
                                            sx={{
                                              width: "100%",
                                              height: 8,
                                              borderRadius: 8,
                                            }}
                                          />
                                        </MDBox>
                                        <MDBox
                                          mt={0.5}
                                          display="flex"
                                          alignItems="center"
                                          justifyContent="space-between"
                                        >
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                          >
                                            {persen}%
                                          </MDTypography>
                                          <MDButton
                                            variant="gradient"
                                            color="info"
                                            size="small"
                                            onClick={() =>
                                              handleDetailClick(pegawai)
                                            }
                                            sx={{ minWidth: "auto", px: 1 }}
                                          >
                                            <Icon sx={{ fontSize: 16 }}>
                                              visibility
                                            </Icon>
                                            &nbsp;Detail
                                          </MDButton>
                                        </MDBox>
                                      </MDBox>
                                    </Grid>
                                  </Grid>
                                </CardContent>
                              </Card>
                            );
                          }
                        )}
                    </MDBox>
                  ) : (
                    <DataTable
                      table={{ columns, rows }}
                      isSorted={false}
                      entriesPerPage={100}
                      showTotalEntries={true}
                      noEndBorder
                      loading={loading}
                      canSearch={false}
                    />
                  )}
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Loading State */}
          {loading && (
            <Grid item xs={12}>
              <Card>
                <MDBox p={3} textAlign="center">
                  <MDTypography variant="body1" color="text">
                    Memuat data rekap dinas...
                  </MDTypography>
                </MDBox>
              </Card>
            </Grid>
          )}

          {/* Empty State */}
          {!dataRekapDinas &&
            !loading &&
            userDinasId &&
            tanggalAwal &&
            tanggalAkhir && (
              <Grid item xs={12}>
                <Card>
                  <MDBox p={3} textAlign="center">
                    <MDBox
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      py={4}
                    >
                      <Icon
                        sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                      >
                        event_busy
                      </Icon>
                      <MDTypography variant="h6" color="text.secondary">
                        Tidak ada data rekap laporan dinas
                      </MDTypography>
                      <MDTypography variant="body2" color="text.secondary">
                        Coba pilih periode tanggal lain atau periksa kembali
                        dinas
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
            )}
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PerdinasDetail;
